import React from 'react';
import { M32ProductTabs } from 'ui/components/3-organisms/M32ProductTabs';
import { ProductTabsItemProps } from 'ui/components/3-organisms/M32ProductTabs/ProductTabsItem';
import { ProductTabsContentProps } from 'ui/components/3-organisms/M32ProductTabs/ProductTabsContent';

const slug = (s?: string) =>
	(s ?? '')
		.toLowerCase()
		.trim()
		.replace(/\s+/g, '-')
		.replace(/[^a-z0-9-_]/g, '');
const stableId = (label: string | undefined, index: number) => (label ? slug(label) : `tab-${index}`);

const isStandard = (t: Umbraco.AnyProductTab): t is Umbraco.ProductTab => t?.documentType === 'productTab';
const isQuadrants = (t: Umbraco.AnyProductTab): t is Umbraco.ProductTab4Quadrants => t?.documentType === 'productTab4Quadrants';

export const M32ProductTabsFeature: React.FC<Umbraco.M32ProductTabs> = ({ content }) => {
	const props = content?.properties;
	const heading = props?.heading;
	const anchor = props?.anchor;

	const tabs: Umbraco.AnyProductTab[] = props?.tabs ?? [];

	// ---- Tab headers (labels) — UI-ready
	const productTabs: ProductTabsItemProps[] = tabs.map((tab, index) => {
		const label = tab.content.properties.heading;
		const id = stableId(label, index);
		return { id, heading: label };
	});

	const productContent: ProductTabsContentProps[] = tabs.map((tab, index) => {
		// STANDARD
		if (isStandard(tab)) {
			const p = tab.content.properties;

			const dataSheetLink = p.dataSheetLink?.url
				? {
						url: p.dataSheetLink.url,
						name: p.dataSheetLink.name,
						target: p.dataSheetLink.target ?? '_self',
				  }
				: undefined;

			const image = p.image?.url
				? {
						url: p.image.url,
						focalPoint: p.image.focalPoint,
				  }
				: undefined;

			const id = stableId(p.heading, index);

			return {
				id,
				kind: 'standard',
				text: p.text,
				image,
				dataSheetTitle: p.dataSheetTitle,
				dataSheetLink,
			};
		}

		// QUADRANTS
		if (isQuadrants(tab)) {
			const p = tab.content.properties;

			const bottomLeftDescription = p.bottomLeftDescription ?? (p as any).BottomLeftDescription;

			const id = stableId(p.heading, index);

			const items: NonNullable<Required<ProductTabsContentProps>['quadrants']>['items'] = [
				{
					position: 'top-left' as const,
					image: p.topLeftImage?.url
						? {
								url: p.topLeftImage.url,
								focalPoint: p.topLeftImage.focalPoint,
						  }
						: undefined,
					title: p.topLeftTitle?.trim(),
					description: p.topLeftDescription?.trim(),
				},
				{
					position: 'top-right' as const,
					image: p.topRightImage?.url
						? {
								url: p.topRightImage.url,
								focalPoint: p.topRightImage.focalPoint,
						  }
						: undefined,
					title: p.topRightTitle?.trim(),
					description: p.topRightDescription?.trim(),
				},
				{
					position: 'bottom-left' as const,
					image: p.bottomLeftImage?.url
						? {
								url: p.bottomLeftImage.url,
								focalPoint: p.bottomLeftImage.focalPoint,
						  }
						: undefined,
					title: p.bottomLeftTitle?.trim(),
					description: bottomLeftDescription?.trim(),
				},
				{
					position: 'bottom-right' as const,
					image: p.bottomRightImage?.url
						? {
								url: p.bottomRightImage.url,
								focalPoint: p.bottomRightImage.focalPoint,
						  }
						: undefined,
					title: p.bottomRightTitle?.trim(),
					description: p.bottomRightDescription?.trim(),
				},
			].filter((it) => it.image?.url || it.title || it.description);

			return {
				id,
				kind: 'quadrants',
				quadrants: {
					heading: p.heading,
					subheading: p.subheading,
					disclaimer: p.disclaimer,
					items,
				},
			};
		}

		// Fallback
		const id = stableId(undefined, index);
		return { id, kind: 'standard', text: '' };
	});

	return <M32ProductTabs heading={heading} anchor={anchor} productTabs={productTabs} productContent={productContent} />;
};
